'use client'
import { useState } from 'react'
import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { Button, Collapse, List, ListItemText, ListItemButton } from '@achieve/ascend'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'

function MapSliderModule({ grid, title }) {
  const items = grid?.gridItemsCollection?.items || []
  const [selected, setSelected] = useState(0)

  const [open, setOpen] = useState(false)

  return (
    <Section
      contain={true}
      variant={'bgDefault'}
      className={'py-10'}
      data-testid="map-slider-section"
    >
      <div className="w-full">
        {title && (
          <Typography
            content={title?.textContent}
            variant="headingLg"
            mobileVariant="headingMd"
            responsive={true}
            className="md:pb-4 pb-4 text-center"
            data-testid="map-slider-title"
          />
        )}

        {items.length > 1 && (
          <div className="mt-2 hidden md:flex items-center justify-center pb-6">
            <div
              className="p-1 rounded-md border border-solid border-gray-300"
              data-testid="map-slider-buttons-container"
            >
              {items.map((item, index) => {
                if (!item || !item?.eyebrow) return null
                return (
                  <Button
                    key={item?.eyebrow}
                    onClick={() => setSelected(index)}
                    color={selected === index ? 'secondary' : 'primary'}
                    variant={selected === index ? 'contained' : 'text'}
                    className="text-[#154199] min-w-[150px]"
                    size="small"
                  >
                    <Typography
                      content={item?.eyebrow}
                      variant="labelSm"
                      fontWeight="bold"
                      className="text-center"
                    />
                  </Button>
                )
              })}
            </div>
          </div>
        )}
        {items.length > 1 && (
          <div className="md:hidden">
            <div className="p-4 w-full max-w-sm mx-auto">
              <List component="nav" aria-labelledby="nested-list-subheader">
                <ListItemButton
                  onClick={() => setOpen(!open)}
                  className="rounded-3xl my-2 py-3  border border-solid border-gray-300  bg-white hover:bg-[#F5F9FF]"
                >
                  <ListItemText primary={items?.[selected]?.eyebrow} className="text-center" />
                  {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List
                    component="div"
                    className="border border-solid border-gray-300 rounded-md"
                    disablePadding
                  >
                    {items.map((item, index) => {
                      if (!item || !item?.eyebrow || selected === index) return null
                      return (
                        <ListItemButton
                          key={item?.sys?.id}
                          className="rounded-md  bg-white text-center p-3  active:bg-[#F5F9FF]"
                          onClick={() => {
                            setSelected(index)
                            setOpen(false)
                          }}
                        >
                          <ListItemText primary={item?.eyebrow} />
                        </ListItemButton>
                      )
                    })}
                  </List>
                </Collapse>
              </List>
            </div>
          </div>
        )}
        <div className="mt-2 flex items-center justify-center">
          <div data-testid="map-slider-image">
            <MediaImageStandard
              content={items?.[selected]?.image}
              layout="fixed"
              className="h-[211px] w-[335px] md:h-[470px] md:w-[745px]  sm:h-[315px] sm:w-[500px]"
              height={470}
              width={745}
              alt={items?.[selected]?.image?.title}
              unoptimized={true}
              fetchPriority="high"
              preload={true}
              quality={100}
            />
          </div>
        </div>

        <div className="container mx-auto p-4">
          <div
            className="flex flex-col sm:flex-row items-start sm:items-center justify-center mb-4"
            data-testid="map-slider-disclaimer-section"
          >
            {items.map((item) => {
              if (!item || !item?.disclaimer) return null
              return (
                <div
                  className="flex items-center mr-4 mb-2"
                  key={item?.sys?.id}
                  data-testid="map-slider-disclaimer-item"
                >
                  <div
                    className={`w-[11px] h-[28px] bg-[${
                      item?.config?.color ? item?.config?.color : '#2C47F6'
                    }] rounded-full mr-2`}
                  ></div>
                  <span className="text-sm">
                    <Typography
                      content={item?.disclaimer}
                      variant="bodyMd"
                      mobileVariant="bodyXs"
                      responsive={true}
                      fontWeight="regular"
                      className="text-center"
                    />
                  </span>
                </div>
              )
            })}
          </div>

          <div className="container mx-auto ">
            <div className="flex flex-row  items-center justify-center">
              <Typography
                content={items?.[selected]?.extraText}
                variant="bodyXs"
                fontWeight="regular"
                className="text-center md:text-left max-w-[830px]"
                data-testid="map-slider-extra-text"
              />
            </div>
          </div>
        </div>
      </div>
    </Section>
  )
}

function MapSlider({ content }) {
  const { grid, title } = useMemoizedContentGetter(content, ['grid', 'title'])

  return <MapSliderModule grid={grid} title={title} />
}

export { MapSlider, MapSliderModule }
export default MapSlider
